import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    PivotGrid,
    FieldChooser,
    Export,
    FieldPanel,
    HeaderFilter,
    Search
} from 'devextreme-react/pivot-grid';
import config from 'devextreme/core/config';
import saveAs from 'file-saver';
import ExcelJS from 'exceljs';
import { exportPivotGrid } from 'devextreme/excel_exporter';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { locale, loadMessages } from 'devextreme/localization';
import enMessages from 'devextreme/localization/messages/en.json';
import deMessages from 'devextreme/localization/messages/de.json';
import plMessages from 'devextreme/localization/messages/pl.json';

const OBDrillDown = (props) => {
    const pivotGridRef = useRef(null);
    const dataSourceRef = useRef(null);
    const [clickTimeout, setClickTimeout] = useState(null);
    const [clickedCell, setClickedCell] = useState(null);
    const [previousCellColor, setPreviousCellColor] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [componentKey, setComponentKey] = useState(0);

    const {
        lng,
        setProps,
        dataSource,
        fieldPanel_showColumnFields,
        fieldPanel_showDataFields,
        fieldPanel_showFilterFields,
        fieldPanel_showRowFields,
        fieldPanel_allowFieldDragging,
        fieldPanel_visible,
        headerFilter_searchEnabled,
        pivotGridState,
        expandCollapseAll,
        cellClickColor,
        cellDblClickColor,
        licenseKey,
        ...pivotGridProps
    } = props;

    config({ licenseKey });

    // Handle DevExtreme locale update
    const loadLanguage = useCallback((lng) => {
        const messages = {
            en: enMessages,
            de: deMessages,
            pl: plMessages
        };
        loadMessages(messages[lng] || enMessages);
        locale(lng);
    }, []);

    useEffect(() => {
        loadLanguage(lng);
        setComponentKey(prev => prev + 1); // Force re-render
    }, [lng, loadLanguage]);

    useEffect(() => {
        const dataSourceConfig = {
            ...dataSource,
            onChanged: handleDataSourceChange,
            onLoadingChanged: setIsLoading
        };

        dataSourceRef.current = new PivotGridDataSource(evaluateCustomSummaryFunctions(dataSourceConfig));

        if (pivotGridRef.current && pivotGridRef.current.instance) {
            pivotGridRef.current.instance.option('dataSource', dataSourceRef.current);
        }
    }, [dataSource, componentKey]);

    useEffect(() => {
        if (pivotGridRef.current && pivotGridRef.current.instance && pivotGridState) {
            const currentState = pivotGridRef.current.instance.getDataSource().state();
            if (JSON.stringify(currentState) !== JSON.stringify(pivotGridState)) {
                pivotGridRef.current.instance.getDataSource().state(pivotGridState);
            }
        }
    }, [pivotGridState, componentKey]);

    useEffect(() => {
        if (expandCollapseAll === null || !dataSourceRef.current) return;
        const fields = dataSourceRef.current.fields();
        fields.forEach((field) => {
            if (field.area === 'row') {
                expandCollapseAll
                    ? dataSourceRef.current.expandAll(field.index)
                    : dataSourceRef.current.collapseAll(field.index);
            }
        });
    }, [expandCollapseAll]);

    const handleDataSourceChange = () => {
        if (setProps && pivotGridRef.current?.instance) {
            const pivotGridState = pivotGridRef.current.instance.getDataSource().state();
            setProps({ pivotGridState });
        }
    };

    const onExporting = (e) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('exportedData');

        exportPivotGrid({
            component: e.component,
            worksheet: worksheet,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'exportedData.xlsx');
            });
        }).catch((error) => {
            console.error("Export failed: ", error);
        });
    };

    const evaluateCustomSummaryFunctions = (dataSource) => {
        if (Array.isArray(dataSource.fields)) {
            dataSource.fields.forEach(field => {
                if (field.calculateCustomSummary) {
                    try {
                        field.calculateCustomSummary = new Function('return (' + field.calculateCustomSummary + ')')();
                    } catch (error) {
                        console.error('Error parsing custom summary function for field:', field, error);
                    }
                }
            });
        }
        return dataSource;
    };

    const handleCellClick = (e) => {
        if (clickTimeout) {
            clearTimeout(clickTimeout);
            setClickTimeout(null);
            handleCellDoubleClick(e);
        } else {
            setClickTimeout(setTimeout(() => {
                updateCellProp(e);
                setClickTimeout(null);
            }, 300));
        }
    };

    const handleCellDoubleClick = (e) => {
        changeCellColor(e.cellElement, cellDblClickColor);
        if (setProps) {
            setProps({ cellDblClick: extractCellInfo(e) });
        }
    };

    const updateCellProp = (e) => {
        changeCellColor(e.cellElement, cellClickColor);
        if (setProps) {
            setProps({ cell: extractCellInfo(e) });
        }
    };

    const extractCellInfo = (e) => ({
        area: e.area,
        cell: e.cell,
        rowIndex: e.rowIndex,
        columnIndex: e.columnIndex,
        columnFields: e.columnFields,
        rowFields: e.rowFields,
        dataFields: e.dataFields
    });

    const changeCellColor = (cellElement, color) => {
        if (clickedCell) {
            clickedCell.style.backgroundColor = previousCellColor;
        }
        setClickedCell(cellElement);
        setPreviousCellColor(cellElement.style.backgroundColor);
        cellElement.style.backgroundColor = color;
    };

    const updateAreaValues = (pgStatefields, dataSourceFields) => {
        const updatedFields = dataSourceFields.map((item, index) => {
            if (pgStatefields[index]?.area) {
                return {
                    ...item,
                    area: pgStatefields[index].area,
                    areaIndex: pgStatefields[index].areaIndex
                };
            }
            return item;
        });

        return updatedFields.sort((a, b) => {
            if (a.area === 'data' && b.area === 'data') {
                return a.areaIndex - b.areaIndex;
            }
            return 0;
        });
    };

    const isDataCell = (cell) => cell.area === 'data' && cell.rowType === 'D' && cell.columnType === 'D';
    const isColumnHeaderCell = (cell) => cell.area === 'column' && ['GT', 'T', 'D'].includes(cell.type);
    const isTotalCell = (cell) => ['GT', 'T'].includes(cell.rowType) || ['GT', 'T'].includes(cell.columnType);

    const getConditionalAppearanceFunction = (cell, functionContent) => {
        try {
            const fn = new Function('cell', functionContent);
            return fn(cell);
        } catch {
            return {};
        }
    };

    const onCellPrepared = ({ cell, area, cellElement }) => {
        if (!cell || !dataSource || !Array.isArray(dataSource.fields)) return;

        cell.area = area;
        cell.dataIndex = cell?.dataIndex;
        if (typeof cell.dataIndex === 'undefined' || cell.dataIndex < 0) return;

        const pgState = pivotGridRef.current.instance.getDataSource().state();
        const dataSourceFields = dataSource.fields;
        const mappedFields = updateAreaValues(pgState.fields, dataSourceFields);
        const dataFields = mappedFields.filter(f => f.area === 'data');

        const cellStyleFn = dataFields[cell.dataIndex]?.dataCellFormatFunction;
        const headerStyleFn = dataFields[cell.dataIndex]?.dataCellHeaderFormatFunction;

        if (cellStyleFn && (isDataCell(cell) || isTotalCell(cell))) {
            const style = getConditionalAppearanceFunction(cell, cellStyleFn);
            Object.assign(cellElement.style, style);
        }

        if (headerStyleFn && isColumnHeaderCell(cell)) {
            const style = getConditionalAppearanceFunction(cell, headerStyleFn);
            Object.assign(cellElement.style, style);
        }
    };

    return (
        <PivotGrid
            key={componentKey}
            {...pivotGridProps}
            ref={pivotGridRef}
            showBorders={true}
            onExporting={onExporting}
            onCellClick={handleCellClick}
            onCellPrepared={onCellPrepared}
        >
            <HeaderFilter>
                {headerFilter_searchEnabled && <Search enabled={true} />}
            </HeaderFilter>
            <FieldChooser enabled={true} />
            <Export enabled={true} />
            <FieldPanel
                showColumnFields={fieldPanel_showColumnFields}
                showDataFields={fieldPanel_showDataFields}
                showFilterFields={fieldPanel_showFilterFields}
                showRowFields={fieldPanel_showRowFields}
                allowFieldDragging={fieldPanel_allowFieldDragging}
                visible={fieldPanel_visible}
            />
        </PivotGrid>
    );
};

OBDrillDown.propTypes = {
    id: PropTypes.string,
    lng: PropTypes.string,
    dataSource: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rtlEnabled: PropTypes.bool,
    disabled: PropTypes.bool,
    visible: PropTypes.bool,
    elementAttr: PropTypes.object,
    hint: PropTypes.string,
    allowExpandAll: PropTypes.bool,
    allowFiltering: PropTypes.bool,
    allowSorting: PropTypes.bool,
    allowSortingBySummary: PropTypes.bool,
    dataFieldArea: PropTypes.string,
    expandCollapseAll: PropTypes.bool,
    fieldChooser: PropTypes.object,
    fieldPanel: PropTypes.object,
    headerFilter: PropTypes.object,
    hideEmptySummaryCells: PropTypes.bool,
    rowHeaderLayout: PropTypes.string,
    scrolling: PropTypes.object,
    showBorders: PropTypes.bool,
    showColumnGrandTotals: PropTypes.bool,
    showColumnTotals: PropTypes.bool,
    showRowGrandTotals: PropTypes.bool,
    showRowTotals: PropTypes.bool,
    stateStoring: PropTypes.object,
    texts: PropTypes.object,
    wordWrapEnabled: PropTypes.bool,
    licenseKey: PropTypes.string,
    cell: PropTypes.object,
    cellClickColor: PropTypes.string,
    cellDblClick: PropTypes.object,
    cellDblClickColor: PropTypes.string,
    exportTest: PropTypes.object,
    setProps: PropTypes.func,
    fieldPanel_showColumnFields: PropTypes.bool,
    fieldPanel_showDataFields: PropTypes.bool,
    fieldPanel_showFilterFields: PropTypes.bool,
    fieldPanel_showRowFields: PropTypes.bool,
    fieldPanel_allowFieldDragging: PropTypes.bool,
    fieldPanel_visible: PropTypes.bool,
    headerFilter_searchEnabled: PropTypes.bool,
    dataSourceChanged: PropTypes.any,
    pivotGridState: PropTypes.object,
};

OBDrillDown.defaultProps = {
    fieldPanel_visible: false,
    lng: 'en',
    cellClickColor: '#F8F8FF',
    cellDblClickColor: '#E6E6FA',
    headerFilter_searchEnabled: true,
    expandCollapseAll: null,
};

export default OBDrillDown;
